// #3427 (scribe-resilience): the ONE definition of "a station is stuck", shared
// by score-api (the once-per-breach alert) and scoreadmin-fe (the banner), so
// the page and the chat alert can never disagree about when a station is stuck.
//
// A station is stuck when its OLDEST unshipped outbox op has been waiting
// longer than the threshold. 3 minutes matches the hand-run S3 snapshot watcher
// used in the Iberian Cup incident (2026-10-16, station 3 sat 57 minutes).
//
// Pure: no clock, no I/O. Callers pass `now`.

export const DEFAULT_STUCK_STATION_THRESHOLD_MS = 3 * 60 * 1000;

// score-api stamps snapshots as `<epoch ms>-<hex>`; ops carry ISO strings.
const EPOCH_MS_STAMP = /^(\d{10,13})(?:-[0-9a-f]+)?$/i;

function toMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value !== "string") return null;
  const stamp = EPOCH_MS_STAMP.exec(value.trim());
  const ms = stamp ? Number(stamp[1]) : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Age of the oldest unshipped op in ms, or null when there is none / it is unreadable. */
export function unshippedAgeMs(oldestUnshippedAt, now) {
  const created = toMs(oldestUnshippedAt);
  const nowMs = toMs(now);
  if (created === null || nowMs === null) return null;
  return Math.max(0, nowMs - created);
}

/**
 * True when the oldest unshipped op is STRICTLY older than the threshold
 * (exactly at the threshold is not yet stuck). Null / unreadable is never stuck.
 * `thresholdMs` falls back to the 3 minute default when missing or not a
 * positive finite number.
 */
export function isStationStuck(oldestUnshippedAt, now, thresholdMs = DEFAULT_STUCK_STATION_THRESHOLD_MS) {
  const age = unshippedAgeMs(oldestUnshippedAt, now);
  if (age === null) return false;
  const limit = Number.isFinite(thresholdMs) && thresholdMs > 0 ? thresholdMs : DEFAULT_STUCK_STATION_THRESHOLD_MS;
  return age > limit;
}

/**
 * Once-per-breach decision. `alreadyAlerted` is the durable "we sent for the
 * current breach" flag. Returns what to do:
 *   "send"    stuck and not yet alerted: send once and set the flag.
 *   "clear"   no longer stuck but flagged: the breach is over, clear the flag
 *             so the NEXT breach alerts again.
 *   "none"    nothing to do (still stuck and already alerted, or healthy).
 */
export function decideStuckAlert({ stuck, alreadyAlerted }) {
  if (stuck && !alreadyAlerted) return "send";
  if (!stuck && alreadyAlerted) return "clear";
  return "none";
}

/**
 * A ring as a person reads it: "Ring 2". The heartbeat stores whatever the
 * scribe's ring was called ("Ring 2" or just "2"), so a value that already
 * starts with "Ring" is left alone and a bare one is prefixed. Null for none.
 */
export function formatRing(ring) {
  if (ring === null || ring === undefined) return null;
  const text = String(ring).trim();
  if (!text) return null;
  return /^ring\b/i.test(text) ? text : `Ring ${text}`;
}
